import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@common/auth/use-auth';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';
import DiarioPontos from '@app/web-player/points-daily/diarios-pontos';
import '@app/web-player/points-daily/PontosDiariosPage.css';
import ShopItem from '@app/web-player/shop/shop-item';

interface DailyPoint {
  id: number;
  img_banner: string;
  title: string;
  description: string;
  qtd_cifras: number;
}

export function PontosDiariosPage() {
  const { user } = useAuth();
  const [dailyPoints, setDailyPoints] = useState<DailyPoint[]>([]);
  const [isCollecting, setIsCollecting] = useState(false);
  const [timer, setTimer] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<DailyPoint | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [isCardSelectionVisible, setIsCardSelectionVisible] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [canCollect, setCanCollect] = useState<boolean | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);


  const fetchDailyPoints = async () => {
    try {
      const response = await fetch('/api/daily_points.php');
      const data = await response.json();
      if (!response.ok) throw new Error('Erro ao carregar os pontos diários');
      setDailyPoints(data);
    } catch (error) {
      console.error(error);
      toast.danger(message('Erro ao carregar os pontos diários'));
    }
  };

  const fetchWalletBalance = async () => {
    if (!user) return;
    try {
      const response = await fetch(`/api/wallet_balance.php?userId=${user.id}`);
      const data = await response.json();
      if (!response.ok) throw new Error('Erro ao carregar o saldo da carteira');
      setWalletBalance(data.wallet_balance);
    } catch (error) {
      console.error(error);
      toast.danger(message('Erro ao carregar o saldo da carteira'));
    }
  };


  const checkCanCollect = async () => {
    if (!user) return;
    try {
      const response = await fetch(`/api/check_daily_collect.php?userId=${user.id}`);
      if (!response.ok) {
        throw new Error('Erro ao verificar coleta diária');
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      setCanCollect(data.canCollect);
    } catch (error) {
      console.error(error);
      toast.danger(message('Erro ao verificar coleta diária'));
      setCanCollect(false);
    }
  };


  const handleCollectPoints = async () => {
    if (!selectedPoint || !user) return;

    const response = await fetch('/api/daily-points-history.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: user.id,
        email: user.email,
        img_banner: selectedPoint.img_banner,
        title: selectedPoint.title,
        qtd_cifras: selectedPoint.qtd_cifras,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      toast.danger(message(data.error || 'Erro ao registrar pontos diários'));
      return;
    }

    const updateBalanceResponse = await fetch(`/api/update_wallet_balance.php?user_id=${user.id}&qtd_cifras=${selectedPoint.qtd_cifras}`);
    
    if (!updateBalanceResponse.ok) {
      toast.danger(message('Erro ao atualizar o saldo da carteira'));
      return;
    }

    const balanceData = await updateBalanceResponse.json();
    if (balanceData.error) {
      toast.danger(message(balanceData.error));
      return;
    }

    setWalletBalance(balanceData.new_balance);
    toast.positive(message(`Cifras diários coletadas! Saldo atualizado: ${balanceData.new_balance}`));
  };

  useEffect(() => {
    fetchDailyPoints();
    if (user) {
      fetchWalletBalance();
      checkCanCollect();
    }
  }, [user]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isCollecting) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev >= 3) {
            clearInterval(interval);
            setIsCollecting(false);
            setIsPulsing(true);
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCollecting]);

  const startCollecting = () => {
    if (isCollecting || !canCollect) return;
    setIsCardSelectionVisible(true);
    setIsCollecting(true);
    setTimer(0);
  };


  const selectRandomCard = () => {
    const randomIndex = Math.floor(Math.random() * dailyPoints.length);
    setSelectedPoint(dailyPoints[randomIndex]);
  };


  useEffect(() => {
    let cardSelectionInterval: NodeJS.Timeout;
    if (isCollecting) {
      cardSelectionInterval = setInterval(selectRandomCard, 50);
      setTimeout(() => {
        clearInterval(cardSelectionInterval);
        setIsPulsing(true);
      }, 3000);
    }
    return () => clearInterval(cardSelectionInterval);
  }, [isCollecting]);

  useEffect(() => {
    if (isPulsing) {
      let pulseCount = 0;
      const pulseInterval = setInterval(() => {
        pulseCount++;
        if (pulseCount >= 6) {
          clearInterval(pulseInterval);
          setIsPulsing(false);
          handleCollectPoints();
        }
      }, 250); // 250ms para cada pulso (125ms expandir + 125ms contrair)
      return () => clearInterval(pulseInterval);
    }
  }, [isPulsing]);

  const closeCardSelection = () => {
    setIsCardSelectionVisible(false);
    setIsCollecting(false);
    setIsPulsing(false);
    setTimer(0);
  };

  return (
    <div className="pontos-diarios-container">
      <h1>Cifras Diárias</h1>
      {!user ? (
        <p>Você precisa estar logado para coletar pontos.</p>
      ) : (
        <>
          <div className="carousel-container">
            <div className="carousel" ref={carouselRef}>
              {dailyPoints.map((point) => (
                <ShopItem
                  key={point.id}
                  id={point.id}
                  title={point.title}
                  description={point.description}
                  price={point.qtd_cifras}
                  imageUrl={point.img_banner}
                  onPurchase={() => {}}
                  hidePurchaseButton={true}
                />
              ))}
            </div>
          </div>
          <div className="collect-info">
            {canCollect !== null && (
              <button 
                className="collect-button" 
                onClick={startCollecting} 
                disabled={isCollecting || !canCollect}
              >
                {isCollecting ? 'Coletando...' : 'Iniciar Resgate'}
              </button>
            )}
            {isCollecting && <p>Tempo: {timer}s</p>}
            {walletBalance !== null && <p className="wallet-balance">Saldo Atual: {walletBalance} cifras</p>}
            {canCollect === false && (
              <p className="collect-message">Você já coletou suas cifras diários hoje. Volte amanhã! 😘</p>
            )}
          </div>
          {isCardSelectionVisible && (
            <div className="card-selection-overlay" onClick={closeCardSelection}>
              <div className="card-selection-container" onClick={(e) => e.stopPropagation()}>
                <button className="close-button" onClick={closeCardSelection}>X</button>
                <DiarioPontos
                  id={selectedPoint?.id || 0}
                  title={selectedPoint?.title || ''}
                  description={selectedPoint?.description || ''}
                  price={selectedPoint?.qtd_cifras || 0}
                  imageUrl={selectedPoint?.img_banner || ''}
                  onPurchase={() => {}}
                  hidePurchaseButton={true}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}